import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import { format } from 'date-fns'
import { TZDate } from '@date-fns/tz'
import clsx from 'clsx'

import {
  Button,
  Flex,
  Skeleton,
  Text,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { normalizeNetworkName } from '~publish/helpers/channels'

import { PaginationControls } from '../components/PaginationControls'
import { TopPostCard } from '../components/TopPostCard'
import { formatInsightsDateRange } from '../utils'
import { SectionCard } from '../components/SectionCard'
import { NoDataEmptyState } from '../components/NoDataEmptyState'
import { PerformanceTableView } from '../AllChannelsInsights/sections/PerformanceTableView'
import {
  TopPostsEmptyCard,
  TopPostsSectionView,
} from '../AllChannelsInsights/sections/TopPostsSectionView'
import { SummaryMetricsContent } from '../components/SummaryMetrics'
import { PublicInsightsPlaceholderSections } from './PublicInsightsPlaceholders'
import type { PublicInsightsSample } from './publicInsightsSampleQuery'

import postStyles from '../AllChannelsInsights/sections/AllChannelsTopPosts.module.css'
import styles from './InsightsSamplePreview.module.css'

export const PUBLIC_INSIGHTS_TOP_POSTS_PER_PAGE = 5

export function PublicInsightsLoadingState(): JSX.Element {
  return (
    <>
      <VisuallyHidden as="output">Loading Buffer’s insights…</VisuallyHidden>
      <PublicInsightsPlaceholderSections loading />
    </>
  )
}

function sampleDateRange(
  sample: PublicInsightsSample,
  { withComparison = true }: { withComparison?: boolean } = {},
): string {
  const previous = withComparison ? sample.previousPeriod : null
  return `${formatInsightsDateRange(
    new TZDate(sample.currentPeriod.start, 'UTC'),
    new TZDate(sample.currentPeriod.end, 'UTC'),
    previous && new TZDate(previous.start, 'UTC'),
    previous && new TZDate(previous.end, 'UTC'),
  )} (UTC)`
}

export function PublicInsightsSummary({
  sample,
}: {
  sample: PublicInsightsSample
}): JSX.Element {
  return (
    <SectionCard>
      <SectionCard.Header>
        <SectionCard.Title>Summary</SectionCard.Title>
        <SectionCard.Subtitle>{sampleDateRange(sample)}</SectionCard.Subtitle>
      </SectionCard.Header>
      <SummaryMetricsContent
        metrics={sample.allChannelsPostInsights.summary}
        loading={false}
        error={undefined}
      />
    </SectionCard>
  )
}

export function PublicInsightsTopPosts({
  sample,
  sort,
  onSortChange,
  page,
  onPageChange,
  loading,
  error,
  onRetry,
}: {
  sample: PublicInsightsSample
  sort: 'reactions' | 'comments'
  onSortChange: (sort: 'reactions' | 'comments') => void
  page: number
  onPageChange: (page: number) => void
  loading: boolean
  error?: string
  onRetry?: () => void
}): JSX.Element {
  const totalPages = Math.ceil(
    sample.topPosts.totalCount / PUBLIC_INSIGHTS_TOP_POSTS_PER_PAGE,
  )

  return (
    <TopPostsSectionView
      title="Top Posts"
      subtitle={sampleDateRange(sample, { withComparison: false })}
      sortBy={sort}
      onSortByChange={onSortChange}
      sortLabel="Rank posts by"
    >
      {loading && (
        <VisuallyHidden as="output">Loading top posts…</VisuallyHidden>
      )}
      {sample.topPosts.edges.length === 0 ? (
        <TopPostsEmptyCard>
          <NoDataEmptyState
            description="No posts for this date range."
            showSuggestion
          />
        </TopPostsEmptyCard>
      ) : (
        <div
          className={clsx(
            postStyles.cardsGridContainer,
            styles.topPostsContent,
          )}
          aria-busy={loading}
        >
          <div
            className={clsx(
              postStyles.cardsGrid,
              (loading || error) && styles.hiddenPosts,
            )}
          >
            {sample.topPosts.edges.map(({ node: post }, index) => {
              const rank =
                (page - 1) * PUBLIC_INSIGHTS_TOP_POSTS_PER_PAGE + index + 1
              const service = normalizeNetworkName(post.channel.service)
              return (
                <TopPostCard key={post.id} label={`Post ${rank} on ${service}`}>
                  <TopPostCard.Header
                    rank={rank}
                    metricValue={
                      post.metrics?.find((metric) => metric.type === sort)
                        ?.value ?? null
                    }
                    metricType={sort}
                  />
                  <TopPostCard.Preview>
                    <TopPostCard.Metadata
                      service={post.channel.service}
                      date={
                        post.sentAt
                          ? format(
                              new TZDate(post.sentAt, 'UTC'),
                              'MMM d, yyyy',
                            )
                          : null
                      }
                      postType={post.metadata?.type}
                    />
                    {post.externalLink ? (
                      <a
                        className={postStyles.postLink}
                        href={post.externalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`View on ${service}`}
                      >
                        <TopPostCard.Content
                          text={post.text}
                          assets={post.assets}
                        />
                      </a>
                    ) : (
                      <TopPostCard.Content
                        text={post.text}
                        assets={post.assets}
                      />
                    )}
                  </TopPostCard.Preview>
                </TopPostCard>
              )
            })}
          </div>
          {loading ? (
            <Skeleton width="100%" height="100%" />
          ) : error ? (
            <Flex
              direction="column"
              gap="sm"
              align="center"
              justify="center"
              role="alert"
            >
              <Text>{error}</Text>
              {onRetry && (
                <Button variant="secondary" onClick={onRetry}>
                  Try again
                </Button>
              )}
            </Flex>
          ) : null}
        </div>
      )}
      {totalPages > 1 && (
        <div className={postStyles.pagination}>
          <PaginationControls
            page={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
            loading={loading}
          />
        </div>
      )}
    </TopPostsSectionView>
  )
}

export type PublicInsightsPerformanceSort = {
  key: string
  direction: 'asc' | 'desc'
}

export function PublicInsightsChannelMetrics({
  sample,
  sortConfig,
  onSort,
}: {
  sample: PublicInsightsSample
  sortConfig: PublicInsightsPerformanceSort
  onSort: (metric: string) => void
}): JSX.Element {
  const rows = sample.allChannelsPostInsights.channelInsights
  const [page, setPage] = useState(1)
  useEffect(() => {
    setPage(1)
  }, [sample.currentPeriod.start, sample.currentPeriod.end])
  return (
    <SectionCard>
      <SectionCard.Header>
        <SectionCard.Title>Performance</SectionCard.Title>
        <SectionCard.Subtitle>{sampleDateRange(sample)}</SectionCard.Subtitle>
      </SectionCard.Header>
      {rows.length === 0 ? (
        <NoDataEmptyState
          description="No channel data for this date range."
          showSuggestion
        />
      ) : (
        <PerformanceTableView
          channelInsights={rows}
          scopeId="public-insights-sample"
          sortConfig={sortConfig}
          onSort={(metric): void => {
            setPage(1)
            onSort(metric)
          }}
          page={page}
          onPageChange={setPage}
          aria-label="Buffer channel performance"
        />
      )}
    </SectionCard>
  )
}
